import { Slider } from "../ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Label } from "../ui/label";
import { Plus, X } from "lucide-react";
import { num } from "../../lib/format";

function NumberInput({ id, value, onChange, prefix, suffix, placeholder, testid }) {
  return (
    <div className="relative">
      {prefix && <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{prefix}</span>}
      <input
        data-testid={testid} id={id} inputMode="decimal" type="text"
        value={value === "" || value === null || value === undefined ? "" : num(value, Number(value) % 1 === 0 ? 0 : 2)}
        placeholder={placeholder}
        onChange={(e) => {
          const raw = e.target.value.replace(/[^0-9.]/g, "");
          onChange(raw === "" ? "" : Number(raw));
        }}
        className={`w-full rounded-lg border border-border bg-white py-2.5 text-sm outline-none transition-colors focus:border-brand focus:ring-2 focus:ring-brand/30 ${prefix ? "pl-7" : "pl-3"} ${suffix ? "pr-10" : "pr-3"}`}
      />
      {suffix && <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{suffix}</span>}
    </div>
  );
}

export default function InputField({ input, value, onChange }) {
  const { id, label, type, help, suffix, options, min, max, step } = input;
  const testid = `input-${id}`;

  if (type === "list") {
    const rows = Array.isArray(value) ? value : input.default;
    const cols = input.columns || [
      { key: "label", label: "Name", type: "text" },
      { key: "value", label: "Amount", type: "currency" },
    ];
    const update = (i, key, v) => {
      const next = rows.map((r, idx) => (idx === i ? { ...r, [key]: v } : r));
      onChange(next);
    };
    const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
    const add = () => {
      const blank = {};
      cols.forEach((c) => (blank[c.key] = c.type === "text" ? "" : 0));
      onChange([...rows, blank]);
    };
    return (
      <div className="space-y-2">
        <Label className="text-sm font-semibold">{label}</Label>
        <div className="hidden gap-2 px-1 text-xs font-medium uppercase tracking-wide text-muted-foreground sm:flex">
          {cols.map((c) => (
            <span key={c.key} className={c.type === "text" ? "flex-1" : "w-28 shrink-0"}>{c.label}</span>
          ))}
          <span className="w-8 shrink-0" />
        </div>
        <div className="space-y-2">
          {rows.map((row, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2 sm:flex-nowrap" data-testid={`${testid}-row-${i}`}>
              {cols.map((c) => (
                c.type === "text" ? (
                  <input key={c.key} data-testid={`${testid}-${i}-${c.key}`} value={row[c.key] ?? ""}
                    onChange={(e) => update(i, c.key, e.target.value)} placeholder={c.label}
                    className="min-w-0 flex-1 rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30" />
                ) : (
                  <div key={c.key} className="relative w-28 shrink-0">
                    {(c.type === "currency") && <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>}
                    <input data-testid={`${testid}-${i}-${c.key}`} inputMode="decimal"
                      value={row[c.key] === "" ? "" : row[c.key]} onChange={(e) => update(i, c.key, e.target.value.replace(/[^0-9.]/g, ""))}
                      className={`w-full rounded-lg border border-border bg-white py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/30 ${c.type === "currency" ? "pl-6 pr-2" : "px-2"}`} />
                  </div>
                )
              ))}
              <button type="button" onClick={() => remove(i)} data-testid={`${testid}-remove-${i}`}
                className="shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive" aria-label="Remove">
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <button type="button" onClick={add} data-testid={`${testid}-add`}
          className="inline-flex items-center gap-1 text-sm font-medium text-brand transition-colors hover:text-action">
          <Plus className="h-4 w-4" /> {input.addLabel || "Add row"}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label htmlFor={id} className="text-sm font-semibold">{label}</Label>
        {type === "slider" && <span className="font-heading text-sm font-bold text-brand" data-testid={`${testid}-value`}>{value}{suffix}</span>}
      </div>

      {type === "select" ? (
        <Select value={String(value)} onValueChange={(v) => {
          const opt = options.find((o) => String(o.value) === v);
          onChange(opt ? opt.value : v);
        }}>
          <SelectTrigger data-testid={testid} className="bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            {options.map((o) => <SelectItem key={String(o.value)} value={String(o.value)} data-testid={`${testid}-opt-${o.value}`}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      ) : type === "slider" ? (
        <div className="pt-2">
          <Slider data-testid={testid} value={[Number(value)]} min={min} max={max} step={step}
            onValueChange={(v) => onChange(v[0])} />
          <div className="mt-1 flex justify-between text-xs text-muted-foreground"><span>{min}{suffix}</span><span>{max}{suffix}</span></div>
        </div>
      ) : (
        <NumberInput id={id} value={value} onChange={onChange} testid={testid}
          prefix={type === "currency" ? "$" : undefined}
          suffix={type === "percent" ? "%" : suffix} />
      )}

      {help && <p className="text-xs text-muted-foreground">{help}</p>}
    </div>
  );
}
